import type { ChangeEvent } from "react";
import type { AssetVisibility } from "@seedance/contracts";
import { canvasCopy } from "../../copy/zh-CN.js";
import { libraryCopy } from "../../library/library-copy.js";

export interface CanvasToolbarProps {
  projectName: string;
  saveStatus: string;
  canUndo: boolean;
  canRedo: boolean;
  search: string;
  onSearchChange(value: string): void;
  onLocate(): void;
  onUndo(): void;
  onRedo(): void;
  onFiles(event: ChangeEvent<HTMLInputElement>): void;
  uploadVisibility: AssetVisibility;
  onUploadVisibilityChange(value: AssetVisibility): void;
  onAddText(): void;
  onAddScript(): void;
  onAddSeedance(): void;
  onOpenLibrary(): void;
  canAddSeedance: boolean;
  bulkSelectionMode: boolean;
  onToggleBulkSelection(): void;
  onPhonePreview(): void;
  onOpenGenerationHistory(): void;
  onToggleAssets(): void;
  onToggleInspector(): void;
  assetsOpen: boolean;
  inspectorOpen: boolean;
}

export function CanvasToolbar(props: CanvasToolbarProps) {
  return (
    <header className="studio-canvas-header">
      <a className="foundation-button foundation-button--ghost" href="/projects">{canvasCopy.back}</a>
      <strong>{props.projectName}</strong>
      <output className="studio-save-state" aria-live="polite">{props.saveStatus}</output>
      <div className="studio-toolbar__history" aria-label={canvasCopy.canvasHistory}>
        <button className="foundation-button foundation-button--ghost" type="button" onClick={props.onUndo} disabled={!props.canUndo}>{canvasCopy.undo}</button>
        <button className="foundation-button foundation-button--ghost" type="button" onClick={props.onRedo} disabled={!props.canRedo}>{canvasCopy.redo}</button>
      </div>
      <label className="studio-toolbar__search">
        <span className="sr-only">{canvasCopy.searchNodes}</span>
        <input value={props.search} onChange={(event) => props.onSearchChange(event.currentTarget.value)} placeholder={canvasCopy.searchNodes} />
      </label>
      <button className="foundation-button foundation-button--ghost" type="button" onClick={props.onLocate} disabled={!props.search.trim()}>{canvasCopy.locate}</button>
      <label className="foundation-button foundation-button--ghost studio-upload-button">
        {canvasCopy.uploadMedia}
        <input type="file" accept="image/*,video/*,audio/*" multiple onChange={props.onFiles} />
      </label>
      <label className="studio-toolbar__upload-visibility">
        <span className="sr-only">上传素材可见范围</span>
        <select
          aria-label="上传素材可见范围"
          value={props.uploadVisibility}
          onChange={(event) => props.onUploadVisibilityChange(event.currentTarget.value as AssetVisibility)}
        >
          <option value="private">保存到我的素材</option>
          <option value="team">共享给团队</option>
        </select>
      </label>
      <button className="foundation-button foundation-button--ghost studio-toolbar__library" type="button" onClick={props.onOpenLibrary}>{libraryCopy.title}</button>
      <button className="foundation-button foundation-button--primary studio-toolbar__desktop-action" type="button" onClick={props.onAddText}>{canvasCopy.addText}</button>
      <button className="foundation-button foundation-button--primary studio-toolbar__desktop-action" type="button" onClick={props.onAddSeedance} disabled={!props.canAddSeedance}>{canvasCopy.seedanceNode}</button>
      <button className="foundation-button foundation-button--ghost studio-toolbar__bulk-select" type="button" aria-pressed={props.bulkSelectionMode} onClick={props.onToggleBulkSelection}>{canvasCopy.bulkSelectMedia}</button>
      <button className="foundation-button foundation-button--primary studio-toolbar__tablet-create" type="button" aria-label={canvasCopy.textNode} onClick={props.onAddText}>{canvasCopy.addText}</button>
      <button className="foundation-button foundation-button--primary studio-toolbar__tablet-create" type="button" aria-label={canvasCopy.seedanceNode} onClick={props.onAddSeedance} disabled={!props.canAddSeedance}>{canvasCopy.seedanceNode}</button>
      <div className="studio-toolbar__phone-create" aria-label="手机创建节点">
        <button type="button" aria-label="手机添加文本" onClick={props.onAddText}>文本</button>
        <button type="button" aria-label="手机添加手动脚本" onClick={props.onAddScript}>脚本</button>
        <button type="button" aria-label="手机添加 Seedance" onClick={props.onAddSeedance} disabled={!props.canAddSeedance}>Seedance</button>
      </div>
      <button className="foundation-button foundation-button--ghost studio-toolbar__tablet-drawer" type="button" aria-pressed={props.assetsOpen} onClick={props.onToggleAssets}>{canvasCopy.assets}</button>
      <button className="foundation-button foundation-button--ghost studio-toolbar__tablet-drawer" type="button" aria-pressed={props.inspectorOpen} onClick={props.onToggleInspector}>{canvasCopy.inspector}</button>
      <button className="foundation-button foundation-button--ghost studio-toolbar__phone-preview" type="button" onClick={props.onPhonePreview}>{canvasCopy.canvasPreview}</button>
      <button className="foundation-button foundation-button--ghost studio-toolbar__account" type="button" aria-label="我的生成记录" onClick={props.onOpenGenerationHistory}>我的生成记录</button>
    </header>
  );
}
